import { cn } from "@/utils";
import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useUserInfo } from "@/api/user";
import { AccountPopover } from "@/components/AccountPopover/AccountPopover";
import SidebarLink from "@/components/Sidebar/SidebarLink";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { ROUTES } from "@/constants/routes";
import {
  Inbox,
  LucideIcon,
  MessageCircle,
  Plug,
  ScrollText,
  Search,
  Settings,
} from "lucide-react";

import { useOpenSettings } from "@/context/OpenSettingsContext";
import { useSidebarContext } from "@/context/SidebarContext";
import { useBreakpoint } from "@/hooks/useBreakpoint";

import { config } from "@/config";
import { useAppBadge } from "@/hooks/useAppBadge";
import { Flags, flagValues } from "@/providers/LaunchDarklyProvider";
import LogoIcon from "@/svg/brand/cohesive-logo-icon.svg?react";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useFeedback } from "../BugReportButton/BugReportButton";
import { GlobalSearch } from "../GlobalSearch/GlobalSearch";

export interface SidebarItem {
  id: string;
  icon: LucideIcon;
  label: string;
  classNames?: string;
  unreadCount?: number;
  requiredFlags?: string[];
  onClick?: () => void;
}

function useSidebarLinks() {
  const lg = useBreakpoint("lg");
  const flags = useFlags<Flags>();
  const navigate = useNavigate();
  const openSettings = useOpenSettings();
  const { setIsDrawerOpen } = useSidebarContext();

  useAppBadge();

  const binLinks: SidebarItem[] = useMemo(() => {
    let baseLinks = [
      {
        id: "inbox",
        icon: Inbox,
        label: "Inbox",
        requiredFlags: [flagValues.inbox],
        onClick: () => {
          navigate(ROUTES.INBOX.ROOT);
          setIsDrawerOpen(false);
        },
      },
    ].filter((link) => {
      if (!link.requiredFlags) return true;
      return link.requiredFlags.every((flag) => flags[flag as keyof Flags]);
    });

    return baseLinks;
  }, [lg, flags, navigate, setIsDrawerOpen]);

  const directLinks: SidebarItem[] = useMemo(() => {
    const baseLinks: SidebarItem[] = [
      {
        id: "skills",
        icon: ScrollText,
        label: "Skills",
        onClick: () => {
          navigate(ROUTES.SKILLS.ROOT);
          setIsDrawerOpen(false);
        },
      },
    ];
    return baseLinks.filter((link) => {
      if (!link.requiredFlags) return true;
      return link.requiredFlags.every((flag) => flags[flag as keyof Flags]);
    });
  }, [lg, flags, navigate, setIsDrawerOpen]);

  const accountLinks: SidebarItem[] = useMemo(
    () => [
      {
        id: "connectors",
        icon: Plug,
        label: "Connectors",
        onClick: () => {
          openSettings(ROUTES.SETTINGS.CONNECTORS);
          setIsDrawerOpen(false);
        },
      },
      {
        id: "admin",
        icon: Settings,
        label: "Settings",
        classNames: "bg-neutral-100 dark:bg-neutral-800 lg:max-w-[300px]",
        onClick: () => {
          openSettings();
          setIsDrawerOpen(false);
        },
      },
    ],
    [openSettings, setIsDrawerOpen],
  );

  return { binLinks, directLinks, accountLinks };
}

const UserAvatar = React.memo(() => {
  const { data: userInfo } = useUserInfo();

  return (
    <Avatar className="h-8 w-8">
      <AvatarImage src={userInfo?.user.picture_url} alt={userInfo?.user.name} />
      <AvatarFallback>{userInfo?.user.name?.charAt(0)}</AvatarFallback>
    </Avatar>
  );
});

interface SidebarItemsProps {
  mode: "expanded" | "collapsed";
}

export const SidebarItems: React.FC<SidebarItemsProps> = ({
  mode = "expanded",
}) => {
  const launchFeedback = useFeedback();
  const navigate = useNavigate();
  const { data: userInfo } = useUserInfo();
  const { setIsDrawerOpen } = useSidebarContext();
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchOpenSource, setSearchOpenSource] = useState<
    "sidebar" | "keyboard_shortcut"
  >("sidebar");

  const { binLinks, directLinks, accountLinks } = useSidebarLinks();
  const openSettings = useOpenSettings();

  // Keyboard shortcut handler (Cmd+K / Ctrl+K)
  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setSearchOpenSource("keyboard_shortcut");
        setIsSearchOpen(true);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  return (
    <div className="h-full flex flex-col overflow-auto">
      <GlobalSearch
        open={isSearchOpen}
        onOpenChange={setIsSearchOpen}
        openSource={searchOpenSource}
      />
      <div className="flex flex-col grow p-5 space-y-4">
        {mode === "collapsed" && (
          <SidebarLink
            mode={mode}
            key="home"
            id="home"
            label="Home"
            icon={() => <LogoIcon width="24px" />}
            onClick={() => {
              navigate(ROUTES.HOME);
              setIsDrawerOpen(false);
            }}
          />
        )}

        <SidebarLink
          mode={mode}
          key="search"
          id="search"
          ariaLabel="Search (⌘K)"
          label={
            <span className="flex items-center gap-2">
              Search
              <kbd className="hidden sm:inline-flex h-5 select-none items-center gap-1 rounded border border-muted/20 bg-muted/20 px-1.5 font-mono text-[10px] font-medium">
                <span className="text-xs">⌘</span>K
              </kbd>
            </span>
          }
          icon={Search}
          onClick={() => {
            setSearchOpenSource("sidebar");
            setIsSearchOpen(true);
          }}
        />

        {binLinks.map((link) => (
          <SidebarLink
            mode={mode}
            key={link.id}
            id={link.id}
            label={link.label}
            icon={link.icon}
            unreadCount={link.unreadCount}
            onClick={link.onClick}
          />
        ))}
        {directLinks.map((link) => (
          <SidebarLink
            mode={mode}
            key={link.id}
            id={link.id}
            label={link.label}
            icon={link.icon}
            unreadCount={link.unreadCount}
            onClick={link.onClick}
          />
        ))}
      </div>
      <div className="p-5 mt-auto space-y-4">
        {accountLinks.map((accountLink) => (
          <SidebarLink
            mode={mode}
            key={accountLink.id}
            id={accountLink.id}
            label={accountLink.label}
            icon={accountLink.icon}
            onClick={accountLink.onClick}
          />
        ))}
        {config.sentry.enabled && (
          <SidebarLink
            mode={mode}
            id="feedback"
            label="Leave Us Feedback"
            icon={MessageCircle}
            onClick={() => {
              launchFeedback();
              setIsDrawerOpen(false);
            }}
          />
        )}
        <div className="pt-4 w-full">
          <AccountPopover popoverSide="bottom" onOpenSettings={openSettings}>
            <SidebarLink
              data-testid="user-avatar"
              mode={mode}
              id="account"
              key="account"
              showTooltip={false}
              label={userInfo?.user?.name ? userInfo.user.name : "Account"}
              icon={UserAvatar}
            />
          </AccountPopover>
        </div>
      </div>
    </div>
  );
};

interface SidebarProps {
  mode: "expanded" | "collapsed";
}

const Sidebar: React.FC<SidebarProps> = () => {
  const lg = useBreakpoint("lg");

  const { isDrawerOpen } = useSidebarContext();

  if (lg) {
    return (
      <div
        key="lg-sidebar"
        className={cn(
          "w-[80px] min-w-[80px] shrink-0 bg-surface-rail duration-200 ease-in-out border-r h-full overflow-auto",
        )}
      >
        <SidebarItems mode="collapsed" />
      </div>
    );
  }

  return (
    <div
      key="md-sidebar"
      className={cn(
        "fixed left-0 top-0 bottom-0 bg-background backdrop-blur-md duration-200 ease-in-out border-r translate-x-0 pt-[48px] z-50",
        !isDrawerOpen && "-translate-x-full",
      )}
    >
      <SidebarItems mode="expanded" />
    </div>
  );
};

export default React.memo(Sidebar);
